import React, { useState } from 'react';
import CopywritingModule from './modules/CopywritingModule';
import HumanizeModule from './modules/HumanizeModule';
import SEOModule from './modules/SEOModule';
import SocialModule from './modules/SocialModule';
import MarketingModule from './modules/MarketingModule';
import HistoryModule from './modules/HistoryModule';

const ProDashboard = ({ user, onLogout, onBack }) => {
  const [activeModule, setActiveModule] = useState('copywriting');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const modules = [
    {
      id: 'copywriting',
      name: 'Copywriting',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
        </svg>
      ),
      badge: 'PRO',
      color: 'green'
    },
    {
      id: 'humanize',
      name: 'Humaniser',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
        </svg>
      ),
      badge: 'IA',
      color: 'purple'
    },
    {
      id: 'seo',
      name: 'SEO',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
        </svg>
      ),
      badge: null,
      color: 'blue'
    },
    {
      id: 'social',
      name: 'Réseaux',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"/>
        </svg>
      ),
      badge: null,
      color: 'pink'
    },
    {
      id: 'marketing',
      name: 'Marketing',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
        </svg>
      ),
      badge: 'BETA',
      color: 'orange'
    },
    {
      id: 'history',
      name: 'Historique',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      ),
      badge: null,
      color: 'gray'
    }
  ];

  const renderModule = () => {
    switch (activeModule) {
      case 'copywriting':
        return <CopywritingModule user={user} />;
      case 'humanize':
        return <HumanizeModule user={user} />;
      case 'seo':
        return <SEOModule user={user} />;
      case 'social':
        return <SocialModule user={user} />;
      case 'marketing':
        return <MarketingModule user={user} />;
      case 'history':
        return <HistoryModule user={user} />;
      default:
        return <CopywritingModule user={user} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-20' : 'w-64'} bg-[#12121a] border-r border-gray-800 flex flex-col transition-all duration-300`}>
        {/* Logo */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center space-x-3">
                <img src="/vfa-logo.png" alt="VF Actions" className="h-8" />
                <div>
                  <span className="text-white font-bold text-sm">VF ACTIONS</span>
                  <span className="block text-[10px] text-green-400 font-medium">PRO</span>
                </div>
              </div>
            )}
            <button 
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="pro-sidebar-nav">
          {modules.map((module) => (
            <button
              key={module.id}
              onClick={() => setActiveModule(module.id)}
              data-testid={`pro-module-${module.id}`}
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} px-3 py-3 rounded-xl transition-all ${
                activeModule === module.id
                  ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/10 text-green-400 border border-green-500/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
              }`}
              title={sidebarCollapsed ? module.name : ''}
            >
              <div className="flex items-center space-x-3">
                {module.icon}
                {!sidebarCollapsed && <span className="font-medium">{module.name}</span>}
              </div>
              {!sidebarCollapsed && module.badge && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                  module.badge === 'PRO' ? 'bg-green-500/20 text-green-400' :
                  module.badge === 'IA' ? 'bg-purple-500/20 text-purple-400' :
                  module.badge === 'BETA' ? 'bg-orange-500/20 text-orange-400' :
                  'bg-gray-700 text-gray-400'
                }`}>
                  {module.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* User & Credits */}
        <div className="p-3 border-t border-gray-800">
          {!sidebarCollapsed && (
            <div className="bg-gray-800/50 rounded-xl p-3 mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400">Crédits restants</span>
                <span className="text-xs text-green-400 font-bold">∞ PRO</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-1.5">
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-1.5 rounded-full w-full"></div>
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center text-white text-sm font-bold">
                {user?.name?.charAt(0) || 'U'}
              </div>
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{user?.name || 'Utilisateur'}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              )}
            </div>
            <button 
              onClick={onLogout}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-red-400"
              title="Déconnexion"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 bg-[#12121a]/80 backdrop-blur-xl border-b border-gray-800 flex items-center justify-between px-6">
          <div className="flex items-center space-x-4">
            <button 
              onClick={onBack}
              data-testid="pro-back-btn"
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">
                {modules.find(m => m.id === activeModule)?.name || 'Dashboard'}
              </h1>
              <p className="text-xs text-gray-500">Générateur de contenu IA professionnel</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => window.open('https://vfactions.eu/aide', '_blank')}
              data-testid="pro-help-btn"
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span>Aide</span>
            </button>
            <button 
              onClick={() => setActiveModule('copywriting')}
              data-testid="pro-new-btn"
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-lg text-sm text-white font-medium transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
              <span>Nouveau</span>
            </button>
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderModule()}
        </div>
      </main>
    </div>
  );
};

export default ProDashboard;
